import {
  useAnimalMedia,
  useClearAnimalCover,
  useUploadAnimalCover,
} from '@/api/hooks';
import { AnimalAvatar } from '@/components/animals/AnimalAvatar';
import { ImageCropModal } from '@/components/ui/ImageCropModal';
import { Modal } from '@/components/ui/Modal';
import { VideoFrameCaptureModal } from '@/components/ui/VideoFrameCaptureModal';
import { getErrorMessage } from '@/lib/api';
import { cn } from '@/lib/cn';
import { loadRemoteImageForCrop, revokeCropObjectUrl } from '@/lib/cropImage';
import { mediaGridPreviewUrl } from '@/lib/mediaPreview';
import type { AnimalMediaItem } from '@/types/api';
import { Camera, ImageIcon, Trash2 } from 'lucide-react';
import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { VideoPreviewThumb } from '@/components/animals/VideoPreviewThumb';

type AnimalCoverPhotoPickerProps = {
  animalId: number;
  internalNumber: string;
  coverPhotoUrl?: string | null;
  sex?: 'male' | 'female';
  avatarSize?: 'sm' | 'md' | 'lg' | 'xl' | '2xl';
  className?: string;
};

export function AnimalCoverPhotoPicker({
  animalId,
  internalNumber,
  coverPhotoUrl,
  sex,
  avatarSize = 'md',
  className,
}: AnimalCoverPhotoPickerProps) {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const { data: media } = useAnimalMedia(animalId);
  const uploadCover = useUploadAnimalCover(animalId);
  const clearCover = useClearAnimalCover(animalId);

  const [menuOpen, setMenuOpen] = useState(false);
  const [menuPosition, setMenuPosition] = useState<{ top: number; left: number } | null>(null);
  const [galleryOpen, setGalleryOpen] = useState(false);
  const [cropSrc, setCropSrc] = useState<string | null>(null);
  const [cropOpen, setCropOpen] = useState(false);
  const [frameVideoSrc, setFrameVideoSrc] = useState<string | null>(null);
  const [frameOpen, setFrameOpen] = useState(false);
  const [pendingOriginalFile, setPendingOriginalFile] = useState<File | null>(null);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');

  const galleryItems = media ?? [];
  const loading = uploadCover.isPending || clearCover.isPending;

  useLayoutEffect(() => {
    if (!menuOpen || !triggerRef.current) {
      setMenuPosition(null);
      return;
    }

    function updatePosition() {
      const trigger = triggerRef.current;
      const menu = menuRef.current;
      if (!trigger) return;

      const rect = trigger.getBoundingClientRect();
      const menuWidth = menu?.offsetWidth ?? 208;
      const menuHeight = menu?.offsetHeight ?? 140;
      const gap = 8;

      let top = rect.bottom + gap;
      let left = rect.left;

      if (left + menuWidth > window.innerWidth - 8) {
        left = window.innerWidth - menuWidth - 8;
      }

      if (top + menuHeight > window.innerHeight - 8) {
        top = rect.top - menuHeight - gap;
      }

      setMenuPosition({ top, left });
    }

    updatePosition();
    window.addEventListener('resize', updatePosition);
    window.addEventListener('scroll', updatePosition, true);

    return () => {
      window.removeEventListener('resize', updatePosition);
      window.removeEventListener('scroll', updatePosition, true);
    };
  }, [menuOpen]);

  useEffect(() => {
    if (!menuOpen) return;

    function handleClick(e: MouseEvent) {
      const target = e.target as Node;
      if (triggerRef.current?.contains(target) || menuRef.current?.contains(target)) {
        return;
      }
      setMenuOpen(false);
    }

    function handleKey(e: KeyboardEvent) {
      if (e.key === 'Escape') setMenuOpen(false);
    }

    document.addEventListener('mousedown', handleClick);
    document.addEventListener('keydown', handleKey);
    return () => {
      document.removeEventListener('mousedown', handleClick);
      document.removeEventListener('keydown', handleKey);
    };
  }, [menuOpen]);

  function closeMenu() {
    if (loading) return;
    setMenuOpen(false);
    setError('');
  }

  function openCropWithFile(file: File) {
    setError('');
    setPendingOriginalFile(file);
    const reader = new FileReader();
    reader.onload = () => {
      setCropSrc(reader.result as string);
      setMenuOpen(false);
      setGalleryOpen(false);
      setCropOpen(true);
    };
    reader.readAsDataURL(file);
  }

  async function openCropWithUrl(url: string) {
    setError('');
    setPendingOriginalFile(null);
    setMenuOpen(false);
    setGalleryOpen(false);
    try {
      const objectUrl = await loadRemoteImageForCrop(url);
      revokeCropObjectUrl(cropSrc);
      setCropSrc(objectUrl);
      setCropOpen(true);
    } catch (err) {
      setError(getErrorMessage(err, 'Não foi possível preparar a foto da galeria para recorte.'));
    }
  }

  function openCoverFromVideo(item: AnimalMediaItem) {
    setError('');
    setPendingOriginalFile(null);
    setMenuOpen(false);
    setGalleryOpen(false);
    setFrameVideoSrc(item.media.url);
    setFrameOpen(true);
  }

  function handleFileChange(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      setError('Selecione um arquivo de imagem.');
      return;
    }
    openCropWithFile(file);
  }

  async function handleConfirmCrop(file: File) {
    setError('');
    try {
      await uploadCover.mutateAsync({
        file,
        originalFile: pendingOriginalFile ?? undefined,
      });
      setMessage('Foto de capa atualizada.');
      setCropOpen(false);
      revokeCropObjectUrl(cropSrc);
      setCropSrc(null);
      setPendingOriginalFile(null);
    } catch (err) {
      setError(getErrorMessage(err, 'Erro ao salvar a foto de capa.'));
      throw err;
    }
  }

  function closeCropModal() {
    if (loading) return;
    setCropOpen(false);
    revokeCropObjectUrl(cropSrc);
    setCropSrc(null);
    setPendingOriginalFile(null);
  }

  async function handleClear() {
    setError('');
    setMessage('');
    try {
      await clearCover.mutateAsync();
      setMessage('Foto de capa removida.');
      closeMenu();
    } catch (err) {
      setError(getErrorMessage(err, 'Erro ao remover a foto de capa.'));
    }
  }

  return (
    <div className={cn('relative shrink-0', className)}>
      <button
        ref={triggerRef}
        type="button"
        onClick={() => setMenuOpen((open) => !open)}
        onPointerDown={(e) => e.stopPropagation()}
        className={cn(
          'rounded-xl transition hover:ring-2 hover:ring-manejai-moss/40 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-manejai-moss',
          menuOpen && 'ring-2 ring-manejai-moss/40',
        )}
        aria-label={`Alterar foto de capa de ${internalNumber}`}
        aria-expanded={menuOpen}
        aria-haspopup="menu"
        title="Alterar foto de capa"
      >
        <AnimalAvatar
          coverPhotoUrl={coverPhotoUrl}
          internalNumber={internalNumber}
          sex={sex}
          size={avatarSize}
        />
      </button>

      {message && !menuOpen && !cropOpen && !galleryOpen && (
        <p className="sr-only" role="status">{message}</p>
      )}

      {error && !menuOpen && (
        <p className="mt-2 max-w-[11rem] text-xs text-red-600">{error}</p>
      )}

      {menuOpen &&
        menuPosition &&
        createPortal(
          <div
            ref={menuRef}
            role="menu"
            style={{ top: menuPosition.top, left: menuPosition.left }}
            className="fixed z-[200] w-52 rounded-xl border border-manejai-sand bg-white py-1 shadow-elevated"
          >
            {error && (
              <p className="px-4 py-2 text-xs text-red-600 border-b border-manejai-sand/60">{error}</p>
            )}
            <CoverMenuItem
              icon={ImageIcon}
              label="Enviar Foto"
              onClick={() => {
                setMenuOpen(false);
                fileInputRef.current?.click();
              }}
              disabled={loading}
            />
            <CoverMenuItem
              icon={Camera}
              label="Da Galeria"
              onClick={() => {
                setMenuOpen(false);
                setGalleryOpen(true);
              }}
              disabled={loading || galleryItems.length === 0}
            />
            {coverPhotoUrl && (
              <>
                <div className="my-1 border-t border-manejai-sand/60" />
                <CoverMenuItem
                  icon={Trash2}
                  label="Remover capa"
                  onClick={handleClear}
                  disabled={loading}
                  danger
                />
              </>
            )}
          </div>,
          document.body,
        )}

      <Modal open={galleryOpen} onClose={() => setGalleryOpen(false)} title="Escolher da galeria" size="md">
        <div className="space-y-3">
          {galleryItems.length === 0 ? (
            <p className="text-sm text-manejai-bark/60">Nenhuma mídia na galeria ainda.</p>
          ) : (
            <div className="grid max-h-56 grid-cols-3 gap-2 overflow-y-auto sm:grid-cols-4">
              {galleryItems.map((item) => (
                <GalleryThumb
                  key={item.id}
                  item={item}
                  onPick={() =>
                    item.media.media_kind === 'video'
                      ? openCoverFromVideo(item)
                      : void openCropWithUrl(item.media.url)
                  }
                />
              ))}
            </div>
          )}
        </div>
      </Modal>

      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        className="hidden"
        onChange={handleFileChange}
      />

      <VideoFrameCaptureModal
        open={frameOpen}
        videoSrc={frameVideoSrc}
        onClose={() => {
          if (loading) return;
          setFrameOpen(false);
          setFrameVideoSrc(null);
        }}
        onCapture={async (objectUrl) => {
          revokeCropObjectUrl(cropSrc);
          setCropSrc(objectUrl);
          setFrameOpen(false);
          setFrameVideoSrc(null);
          setCropOpen(true);
        }}
        loading={loading}
      />

      <ImageCropModal
        open={cropOpen}
        imageSrc={cropSrc}
        title="Ajustar capa"
        description={
          pendingOriginalFile
            ? 'A foto inteira ficará na galeria; o recorte será a capa.'
            : 'Mova e redimensione o quadro sobre a imagem.'
        }
        confirmLabel="Definir como capa"
        aspect={1}
        outputFileName="animal-cover.jpg"
        onClose={closeCropModal}
        onConfirm={handleConfirmCrop}
        loading={loading}
      />
    </div>
  );
}

function CoverMenuItem({
  icon: Icon,
  label,
  onClick,
  disabled = false,
  danger = false,
}: {
  icon: React.ComponentType<{ className?: string }>;
  label: string;
  onClick: () => void;
  disabled?: boolean;
  danger?: boolean;
}) {
  return (
    <button
      type="button"
      role="menuitem"
      disabled={disabled}
      onClick={onClick}
      className={cn(
        'flex w-full items-center gap-3 px-4 py-2.5 text-sm transition-colors disabled:cursor-not-allowed disabled:opacity-50',
        danger
          ? 'text-red-600 hover:bg-red-50'
          : 'text-manejai-bark hover:bg-manejai-cream/50',
      )}
    >
      <Icon className={cn('h-4 w-4 shrink-0', danger ? 'text-red-500' : 'text-manejai-moss')} />
      {label}
    </button>
  );
}

function GalleryThumb({
  item,
  onPick,
}: {
  item: AnimalMediaItem;
  onPick: () => void;
}) {
  const isVideo = item.media.media_kind === 'video';

  return (
    <button
      type="button"
      onClick={onPick}
      className="relative aspect-square overflow-hidden rounded-lg ring-1 ring-manejai-sand transition hover:ring-manejai-moss"
      title={isVideo ? 'Capturar capa do vídeo' : 'Usar foto como capa'}
    >
      {isVideo ? (
        <VideoPreviewThumb
          videoUrl={item.media.url}
          thumbnailUrl={item.media.thumbnail_url}
          alt="Vídeo da galeria"
        />
      ) : (
        <img
          src={mediaGridPreviewUrl(item.media)}
          alt="Foto da galeria"
          loading="lazy"
          decoding="async"
          className="h-full w-full object-cover"
        />
      )}
      {item.is_primary && (
        <span className="absolute left-1 top-1 rounded bg-manejai-forest/85 px-1.5 py-0.5 text-[10px] font-medium text-white">
          Capa
        </span>
      )}
    </button>
  );
}
